import { isValidDate } from "@components/inputs/date/utils";
import BindingContext from "@odata/BindingContext";
import { IPrWorkingPatternDayIntervalEntity, PrWorkingPatternDayIntervalEntity } from "@odata/GeneratedEntityTypes";
import { PrWorkingPatternIntervalTypeCode } from "@odata/GeneratedEnums";
import { Dayjs } from "dayjs";
import i18next from "i18next";

import { ValidationErrorType } from "../../../enums";
import { getUtcDayjs } from "../../../types/Date";
import { FormStorage } from "../../../views/formView/FormStorage";

export const MIN_INTERVAL_MINUTES = 30;

/**
 * Returns the duration of an interval in minutes.
 * Handles the case where the interval spans midnight (IsStartNextDay / IsEndNextDay flags).
 */
export function getIntervalDurationMinutes(interval: IPrWorkingPatternDayIntervalEntity): number {
    const start = toAbsoluteMinutes(interval.TimeStart, interval.IsStartNextDay);
    const end = toAbsoluteMinutes(interval.TimeEnd, interval.IsEndNextDay);
    return end - start;
}

/**
 * Converts a Date + nextDay flag to an absolute minute count from the start of day 1.
 * Day 1: 0–1439, Day 2: 1440–2879
 */
export function toAbsoluteMinutes(date: Date | Dayjs, isNextDay: boolean): number {
    const d = getUtcDayjs(date);
    const minutes = d.hour() * 60 + d.minute();
    return isNextDay ? 1440 + minutes : minutes;
}

export type ChangeType = "start" | "end" | "delete";

export function normalizeIntervals(intervals: IPrWorkingPatternDayIntervalEntity[], fromIdx: number): void {
    let idx = fromIdx;
    let isNextDay = false;
    while (idx < intervals.length) {
        const interval = intervals[idx];
        const prevInterval = idx > 0 ? intervals[idx - 1] : null;
        if (isValidDate(interval.TimeStart) && isValidDate(interval.TimeEnd)) {
            const timeStart = getUtcDayjs(interval.TimeStart);
            const timeEnd = getUtcDayjs(interval.TimeEnd);
            isNextDay = isNextDay || !!prevInterval?.IsEndNextDay;
            interval.IsStartNextDay = isNextDay;
            isNextDay = isNextDay || toAbsoluteMinutes(timeStart, false) > toAbsoluteMinutes(timeEnd, false);
            interval.IsEndNextDay = isNextDay;
        }
        ++idx;
    }
}

/**
 * If all intervals start on day 2 (IsStartNextDay === true for first),
 * move them all back to day 1 and clear flags.
 */
function normalizeFirstDayFlag(intervals: IPrWorkingPatternDayIntervalEntity[]): void {
    if (intervals[0]?.IsStartNextDay) {
        // we moved all intervals to the next day, move them back to the first day and switch the flag off
        intervals[0].IsStartNextDay = false;
        normalizeIntervals(intervals, 0);
    }
}

export function normalizeIntervalsAfterChange(intervals: IPrWorkingPatternDayIntervalEntity[], changeType: ChangeType, changedIdx: number): void {
    if (changeType === "delete") {
        // changedIdx is deleted index -> the next one is new current. Set its start to be end of previous
        const currInterval = intervals[changedIdx];
        const prevInterval = changedIdx > 0 ? intervals[changedIdx - 1] : null;
        if (currInterval && prevInterval) {
            currInterval.TimeStart = prevInterval.TimeEnd;
            currInterval.IsStartNextDay = prevInterval.IsEndNextDay;
            normalizeIntervals(intervals, changedIdx);
        }
    } else {
        const changedDateVal = intervals[changedIdx][changeType === "start" ? "TimeStart" : "TimeEnd"];

        if (changeType === "start" && isValidDate(changedDateVal as Date)) {
            const prevInterval = changedIdx > 0 ? intervals[changedIdx - 1] : null;
            if (prevInterval) {
                // make timeTo same as current value
                prevInterval.TimeEnd = changedDateVal as Date;
                // set isNextDay flag
                normalizeIntervals(intervals, changedIdx - 1);
            } else {
                normalizeIntervals(intervals, changedIdx);
            }
        }

        if (changeType === "end" && isValidDate(changedDateVal)) {
            const nextInterval = changedIdx < intervals.length - 1 ? intervals[changedIdx + 1] : null;
            if (nextInterval) {
                nextInterval.TimeStart = changedDateVal as Date;
                // next interval is checked in the loop below
            }
            normalizeIntervals(intervals, changedIdx);
        }
    }

    normalizeFirstDayFlag(intervals);
}

export function getIntervalIndex(intervals: IPrWorkingPatternDayIntervalEntity[], bc: BindingContext): number {
    const currKey = bc.getKey();
    const baseBc = bc.removeKey();
    return intervals.findIndex(i => {
        const key = baseBc.addKey(i).getKey();
        return key === currKey;
    });
}

export function validateInterval(interval: IPrWorkingPatternDayIntervalEntity, intervalBc: BindingContext, storage: FormStorage): void {
    const startMinutes = toAbsoluteMinutes(interval.TimeStart, !!interval.IsStartNextDay);
    const endMinutes = toAbsoluteMinutes(interval.TimeEnd, !!interval.IsEndNextDay);
    const timeEndBc = intervalBc.navigate(PrWorkingPatternDayIntervalEntity.TimeEnd);
    if (endMinutes - startMinutes < MIN_INTERVAL_MINUTES && interval.Type.Code !== PrWorkingPatternIntervalTypeCode.MealBreak) {
        storage.setError(timeEndBc, {
            errorType: ValidationErrorType.Field,
            message: i18next.t("WorkingPatterns:TimeEndPreceedingTimeStart")
        });
        return;
    } else {
        storage.clearError(timeEndBc);
    }
}

export function validateIntervals(intervals: IPrWorkingPatternDayIntervalEntity[], storage: FormStorage, intervalsBc: BindingContext): void {
    intervals?.forEach((interval) => {
        validateInterval(interval, intervalsBc.addKey(interval), storage);
    });
}

export function getTimeSpanFromHours(hours: number): Date {
    const minutes = Math.round((hours % 1) * 60);
    return getUtcDayjs().startOf("day").add(Math.floor(hours), "hour").add(minutes, "minute").toDate();
}